// ==========================================
// src/components/layout/Footer.js
// ==========================================
import React, { useState } from 'react';
import { X } from 'lucide-react';

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 z-10">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto">
            {children}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 sticky bottom-0">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Content
const PrivacyPolicyContent = () => (
  <div className="prose prose-sm max-w-none">
    <p className="text-gray-600 mb-6">
      <strong>Effective Date:</strong> January 1, 2025
    </p>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h3>
      <p className="text-gray-600 leading-relaxed">
        SYBR ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
        use, disclose, and safeguard your information when you use our transportation management system and related services 
        (collectively, the "Services").
      </p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Information You Provide</h4>
          <ul className="list-disc pl-6 text-gray-600 space-y-1">
            <li>Account information (name, email, phone number, company details)</li>
            <li>Shipment data (origin, destination, cargo details, delivery information)</li>
            <li>Driver and vehicle information</li>
            <li>Payment and billing information</li>
            <li>Communications with our support team</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Automatically Collected Information</h4>
          <ul className="list-disc pl-6 text-gray-600 space-y-1">
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage data (features used, pages viewed, time spent)</li>
            <li>Location data (when using mobile features)</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </div>
      </div>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h3>
      <ul className="list-disc pl-6 text-gray-600 space-y-2">
        <li>Provide, maintain, and improve our Services</li>
        <li>Process shipments and manage logistics operations</li>
        <li>Communicate with you about your account and services</li>
        <li>Process payments and prevent fraud</li>
        <li>Comply with legal obligations and enforce our agreements</li>
        <li>Analyze usage patterns to enhance user experience</li>
        <li>Send marketing communications (with your consent)</li>
      </ul>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">4. Information Sharing</h3>
      <p className="text-gray-600 mb-3">We may share your information with:</p>
      <ul className="list-disc pl-6 text-gray-600 space-y-2">
        <li><strong>Service Providers:</strong> Third parties who perform services on our behalf (payment processing, hosting, analytics)</li>
        <li><strong>Business Partners:</strong> Carriers, shippers, and other logistics partners necessary to fulfill shipments</li>
        <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
        <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
      </ul>
      <p className="text-gray-600 mt-3">We do not sell your personal information to third parties.</p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">5. Data Security</h3>
      <p className="text-gray-600">
        We implement industry-standard security measures to protect your information, including encryption, 
        secure servers, and access controls. However, no method of transmission over the internet is 100% secure, 
        and we cannot guarantee absolute security.
      </p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">6. Contact Us</h3>
      <p className="text-gray-600">
        For questions about this Privacy Policy, contact us at:
      </p>
      <div className="mt-3 text-gray-600">
        <p>Email: info@sybr.org</p>
        <p>Mail: SYBR LLC, 1 Center Square, Suite 203A, Hanover, PA 17331</p>
      </div>
    </section>
  </div>
);

// Terms of Service Content
const TermsOfServiceContent = () => (
  <div className="prose prose-sm max-w-none">
    <p className="text-gray-600 mb-6">
      <strong>Effective Date:</strong> January 1, 2025
    </p>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h3>
      <p className="text-gray-600 leading-relaxed">
        By accessing or using SYBR's transportation management system and related services (the "Services"), you agree to be 
        bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use our Services.
      </p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">2. Description of Services</h3>
      <p className="text-gray-600 leading-relaxed">
        SYBR provides a cloud-based transportation management platform that enables freight carriers and logistics operators 
        to manage dispatch operations, track shipments, process compliance documentation, and streamline freight operations.
      </p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">3. Account Registration</h3>
      <ul className="list-disc pl-6 text-gray-600 space-y-2">
        <li>You must provide accurate and complete information during registration</li>
        <li>You are responsible for maintaining the confidentiality of your account credentials</li>
        <li>You are responsible for all activities that occur under your account</li>
        <li>You must notify us immediately of any unauthorized access</li>
        <li>You must be at least 18 years old to create an account</li>
      </ul>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">4. User Responsibilities</h3>
      <p className="text-gray-600 mb-3">You agree to:</p>
      <ul className="list-disc pl-6 text-gray-600 space-y-2">
        <li>Use the Services only for lawful purposes and in compliance with all applicable laws</li>
        <li>Provide accurate shipment and carrier information</li>
        <li>Maintain proper licensing and insurance required for freight operations</li>
        <li>Not interfere with or disrupt the Services or servers</li>
        <li>Not attempt to gain unauthorized access to any part of the Services</li>
      </ul>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">5. Intellectual Property</h3>
      <p className="text-gray-600 leading-relaxed">
        The Services, including all software, content, trademarks, and logos, are owned by SYBR and protected by intellectual 
        property laws. You are granted a limited, non-exclusive, non-transferable license to use the Services for your business operations.
      </p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">6. Limitation of Liability</h3>
      <p className="text-gray-600 leading-relaxed">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, SYBR IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. 
        We are not liable for indirect, incidental, or consequential damages arising from your use of the Services.
      </p>
    </section>

    <section className="mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-3">7. Contact Information</h3>
      <p className="text-gray-600">
        For questions about these Terms, contact us at:
      </p>
      <div className="mt-3 text-gray-600">
        <p>Email: info@sybr.org</p>
        <p>Mail: SYBR LLC, 1 Center Square, Suite 203A, Hanover, PA 17331</p>
      </div>
    </section>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  
  const footerLinks = {
    contact: {
      title: 'Contact',
      links: [
        { text: '1 Center Square, Suite 203A', isAddress: true },
        { text: 'Hanover, PA 17331', isAddress: true },
        { text: '(877) 847-7279', href: 'tel:8778477279' },
        { text: 'info@sybr.org', href: 'mailto:info@sybr.org' }
      ]
    },
    getStarted: {
      title: 'Get Started',
      links: [
        { text: 'Book a Demo', href: 'https://calendly.com/sybrtms/intro-call' },
        { text: 'Learn More', href: '#keap-form' },
        { text: 'Email Us', href: 'mailto:info@sybr.org' },
      ]
    }
  };

  return (
    <>
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="md:col-span-2">
              <div className="mb-4">
                <span className="text-2xl font-bold">SYBR ®</span>
                <span className="ml-3 text-gray-400">Software Your Business Remembers</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                When logistics tools don't talk, loads stall. SYBR's TMS, Data Bridge, Track, 
                and automation connect your ops so freight moves itself—from tender to POD.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://calendly.com/sybrtms/intro-call" 
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                  target='_blank'
                  rel="noopener noreferrer"
                >
                  Book Demo
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{footerLinks.contact.title}</h3>
              <div className="space-y-2 text-gray-400">
                {footerLinks.contact.links.map((link, index) => (
                  link.isAddress ? (
                    <p key={index}>{link.text}</p>
                  ) : (
                    <p key={index}>
                      <a href={link.href} className="hover:text-white transition">
                        {link.text}
                      </a>
                    </p>
                  )
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{footerLinks.getStarted.title}</h3>
              <div className="space-y-2">
                {footerLinks.getStarted.links.map((link, index) => (
                  <a 
                    key={index}
                    href={link.href} 
                    className="block text-gray-400 hover:text-white transition"
                  >
                    {link.text}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © {currentYear} SYBR LLC. All rights reserved.
              </p>
              <div className="flex gap-6 mt-4 md:mt-0">
                <button 
                  onClick={() => setPrivacyOpen(true)}
                  className="text-gray-400 hover:text-white transition text-sm underline"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => setTermsOpen(true)}
                  className="text-gray-400 hover:text-white transition text-sm underline"
                >
                  Terms of Service
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy">
        <PrivacyPolicyContent />
      </Modal>

      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service">
        <TermsOfServiceContent />
      </Modal>
    </>
  );
};

export default Footer;